import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Paper,
  TextField,
  Collapse,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { dateFormate } from '../../utils/helper.jsx';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useMediaQuery, useTheme } from '@mui/material';
import OrganisationDropdown from '../Shared/OrganisationDropdown/index.tsx';
import ModalForms from '../Shared/Modals/index.js';
import { useRecoilState } from 'recoil';
import { aiStateAtom, projectAndQueryListAtom } from '../../Services/_states/index.js';
import CampaignDropdown from '../Shared/CampaignDropdown/index.tsx';
import { AiActions } from '../../Services/_actions/index.js';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import DownloadIcon from '@mui/icons-material/Download';

interface Query {
  id: number;
  name: string;
  query?: string;
  status: 'pending' | 'completed' | 'error';
}

interface Project {
  organisation_id: string;
  project_name: string;
  campaign_id: string;
}

interface LeftSidebarProps {
  // queries: Query[];
  editName: string;
  setEditName: (name: string) => void;
  cancelEdit: () => void;
  onSelectQuery: (query: Query) => void;
  // onOrganisationChange: (organisationId: string | null) => void;
  // addQuery: (query: Query) => void; // Commenting out as new chat initiation is handled by parent
  selectedQuery: any;
  onInitiateNewChat: () => void; // New prop to handle new chat session
}

interface PhoneNumber {
  code: string;
  number: string;
}

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #ccc',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const LeftSidebar: React.FC<LeftSidebarProps> = ({
  queries,
  selectedQuery,
  editName,
  setEditName,
  cancelEdit,
  onSelectQuery,
  onInitiateNewChat,
}) => {
  const { showSnackBar } = useSnackBar();
  const [chatInputState, setChatInputState] = useRecoilState(aiStateAtom);
  const [projectAndQueryList, setProjectAndQueryList] =
    useRecoilState<any>(projectAndQueryListAtom);
  const [projects, setProjects] = useState<Project[]>([]);
  const [collapsedChats, setCollapsedChats] = useState(false);
  const [openNewProjectModal, setOpenNewProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [visibleProjectId, setVisibleProjectId] = useState<number | null>(null);
  const [editProjectId, setEditProjectId] = useState<number | null>(null);
  const [editProjectName, setEditProjectName] = useState('');
  const [collapsedProjects, setCollapsedProjects] = useState(false);
  const isSmallScreen = useMediaQuery('(max-width:1024px)');
  const [drawerOpen, setDrawerOpen] = useState(!isSmallScreen);
  const [editingProjectChat, setEditingProjectChat] = useState<{
    projectId: number;
    chatId: number;
  } | null>(null);
  const aiActions = AiActions();

  const handleViewBenchmarkReport = async (conversation_id: string, project_id?: string) => {
    try {
      const res = await aiActions.getBenchmarkReport({
        conversation_id,
        project_id,
      });
      window.open(
        `/r/ai/benchmarking-report?chat_id=${conversation_id}&project_id=${project_id ?? ''}`,
        '_blank'
      );
    } catch (error) {
      showSnackBar('Failed to load benchmark report', 'error');
      console.error(error);
    }
  };

  const handleDownloadBenchmarkReport = (conversation_id?: string, project_id?: string) => {
    try {
      const query = new URLSearchParams();
      if (conversation_id) query.append('conversation_id', conversation_id);
      if (project_id) query.append('project_id', project_id);

      const url = `${process.env.REACT_APP_API_URL}/v0/ui/ai-agent/download-benchmark-report/?${query.toString()}`;
      window.location.href = url;
      showSnackBar('Benchmark report download started.', 'success');
    } catch (error) {
      showSnackBar('Failed to download benchmark report', 'error');
      console.error('❌ Error downloading benchmark report:', error);
    }
  };

  const effectiveDrawerOpen = isSmallScreen ? drawerOpen : true;
  const theme = useTheme();
  const drawerWidth = drawerOpen ? '100%' : 60; // Adjust as needed

  const handleToggleSection = (section: 'projects' | 'chats') => {
    if (section === 'projects') {
      setCollapsedProjects((prev) => {
        const newState = !prev;
        if (!newState) setCollapsedChats(true); // Collapse chats when opening projects
        return newState;
      });
    } else {
      setChatInputState((prev) => ({ ...prev, selectedProject: null }));
      setCollapsedChats((prev) => {
        const newState = !prev;
        if (!newState) setCollapsedProjects(true); // Collapse projects when opening chats
        return newState;
      });
    }
  };

  const handleAddNewProject = async () => {
    if (
      !newProjectName.trim() ||
      !chatInputState?.selectedOrganisation?.organisation_id ||
      !chatInputState?.selectedCampaign?.proposal_id
    ) {
      showSnackBar('Project name,Organisation and Campaign are required', 'warning');
      return;
    }
    const newProject: Project = {
      organisation_id: chatInputState?.selectedOrganisation?.organisation_id ?? '',
      campaign_id: chatInputState?.selectedCampaign?.proposal_id ?? '',
      project_name: newProjectName,
    };
    try {
      const response = await aiActions.createProject(newProject);
      if (response?.status === 200) {
        // setProjects((prev) => ({ ...prev, [newProject.campaign_id]: { ...response?.data?.data } }));
        setProjects((prev) => ({
          ...prev,
          [newProject.campaign_id]: [...(prev[newProject.campaign_id] ?? []), response?.data?.data],
        }));
        showSnackBar('Project created successfully', 'success');
        setOpenNewProjectModal(false);
        setNewProjectName('');
      }
    } catch (error) {
      console.warn(error);
    }
  };

  const getProjectList = async (queryParams: { campaign_id: string; [key: string]: any }) => {
    if (!queryParams?.campaign_id) {
      showSnackBar('Missing campaign_id in queryParams', 'warning');
      return;
    }
    if (projects?.[queryParams?.campaign_id]) return;

    try {
      const response = await aiActions.getProjectList(queryParams);
      if (response?.status === 200 && Array.isArray(response?.data)) {
        setProjects((prev) => ({
          ...prev,
          [queryParams.campaign_id]: [...response.data],
        }));
      } else {
        setProjects((prev) => ({
          ...prev,
          [queryParams.campaign_id]: [],
        }));
      }
    } catch (error) {
      setProjects((prev) => ({
        ...prev,
        [queryParams.campaign_id]: [],
      }));
    }
  };

  const handleAddChatToProject = (project: number) => {
    // Remove prompt and directly initiate chat creation
    // handleToggleProjectChats(project)
    onInitiateNewChat();
    setChatInputState((prevState) => ({
      ...prevState,
      query: '',
      conversation_id: undefined,
      selectedProject: { project_id: project?.project_id },
    }));
  };

  // const handleAddChatToProject = (projectId: number) => {
  //   const chatName = prompt('Enter chat name');
  //   if (!chatName) return;
  //   setProjects((prev) =>
  //     prev.map((proj) =>
  //       proj.id === projectId
  //         ? {
  //             ...proj,
  //             chats: [...proj.chats, { id: Date.now(), name: chatName, status: 'pending' }],
  //           }
  //         : proj
  //     )
  //   );
  // };

  const handleEditProject = (projectId: number, name: string) => {
    setEditProjectId(projectId);
    setEditProjectName(name);
  };

  const handleSaveProjectChatName = () => {
    if (!editingProjectChat) return;
    const { projectId, chatId } = editingProjectChat;

    setProjects((prev) =>
      prev.map((project) =>
        project.project_id === projectId
          ? {
              ...project,
              chats: project.chats.map((chat) =>
                chat.id === chatId ? { ...chat, name: editName } : chat
              ),
            }
          : project
      )
    );

    setEditingProjectChat(null);
    setEditName('');
  };

  const handleDeleteProjectChat = (projectId: number, chatId: number) => {
    setProjects((prev) =>
      prev.map((project) =>
        project.project_id === projectId
          ? {
              ...project,
              chats: project.chats.filter((chat) => chat.id !== chatId),
            }
          : project
      )
    );
  };

  const handleSaveProjectName = () => {
    setProjects((prev) =>
      prev.map((proj) => (proj.id === editProjectId ? { ...proj, name: editProjectName } : proj))
    );
    setEditProjectId(null);
    setEditProjectName('');
  };

  const handleDeleteProject = (projectId: number) => {
    setProjects((prev) => prev.filter((proj) => proj.id !== projectId));
  };

  const getConversationList = async (queryParams) => {
    try {
      if (projectAndQueryList?.[queryParams.campaign_id]) return;
      const response = await aiActions.getConversationList(queryParams);
      if (response.status === 200) {
        //  setProjectAndQueryList((prev) => ({ ...prev, [queryParams.campaign_id]: response?.data }));
        if (Array.isArray(response?.data)) {
          const projectWiseGrouped = {};
          const globalChats = [];
          response.data.forEach((chat) => {
            if (chat.project_id) {
              if (!projectWiseGrouped[chat.project_id]) {
                projectWiseGrouped[chat.project_id] = [];
              }
              projectWiseGrouped[chat.project_id].push(chat);
            } else {
              globalChats.push(chat);
            }
          });
          setProjectAndQueryList((prev) => ({
            ...prev,
            ...projectWiseGrouped,
            [queryParams.campaign_id]: globalChats ?? [],
          }));
        }
      } else {
        setProjectAndQueryList((prev) => ({ ...prev, [queryParams.campaign_id]: [] }));
        throw new Error('Failed to fetch conversation list');
      }
    } catch (error) {
      console.log(error);
    } finally {
      // setLoading(false);
    }
  };

  const handleToggleProjectChats = async (project: number) => {
    const newVisibleProjectId =
      visibleProjectId === project?.project_id ? null : project?.project_id;
    setVisibleProjectId(newVisibleProjectId);

    if (newVisibleProjectId !== null) {
      // Close chats section when a project is opened
      setCollapsedChats(true);
      setCollapsedProjects(false);
    }

    setChatInputState((prev) => ({ ...prev, selectedProject: project }));

    let queryParams = {
      organisation_id: chatInputState?.selectedOrganisation?.organisation_id,
      campaign_id: chatInputState?.selectedCampaign?.proposal_id,
      project_id: project?.project_id,
    };

    try {
      if (projectAndQueryList?.[project?.project_id]) return;
      const response = await aiActions.getConversationList(queryParams);
      if (response.status === 200) {
        setProjectAndQueryList((prev) => ({ ...prev, [project?.project_id]: response?.data }));
        return response.data;
      } else {
        setProjectAndQueryList((prev) => ({ ...prev, [project?.project_id]: [] }));
        throw new Error('Failed to fetch conversation list');
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!isSmallScreen) {
      setDrawerOpen(true);
    }
  }, [isSmallScreen]);

  useEffect(() => {
    const orgId = chatInputState?.selectedOrganisation?.organisation_id;
    const campaignId = chatInputState?.selectedCampaign?.proposal_id;
    if (orgId && campaignId) {
      let queryParams = {
        organisation_id: orgId,
        campaign_id: campaignId,
      };
      getProjectList(queryParams);
      getConversationList(queryParams);
    }
  }, [chatInputState]);

  return (
    <Paper
      sx={{
        p: {xl:1,md:2,sm:2,xs:2},
        py:{md:1},
        height: {xl:'85vh',lg:'85vh',md:"85vh",sm:'80vh',xs:'89vh'},
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        width: drawerWidth,
        maxWidth: { xs:'65vw',sm:'38vw', md: '38vw' },
        transition: 'all 0.3s ease',
        // background:{xs:'transparent',sm:'white', md:'white'},
        backgroundColor: { xs: drawerOpen ? 'white' : 'transparent' },
        // boxShadow: { xs: '0 0 0 0', sm: 'auto', md: 'auto' },
        position: { xs: 'absolute', sm:'absolute', md: 'absolute', lg: 'unset' },
        right: { xs: '0px', sm: '0', md: 'unset' },
        top: { xs: '58px', sm: '66px', md: '66px' },
        boxShadow: {xs: drawerOpen ? '1px 0px 4px 0px #00000052' : 'none', sm: drawerOpen ? '1px 0px 4px 0px #00000052' : 'none', md: drawerOpen ? '1px 0px 4px 0px #00000052' : 'none'},
        left: {
            xs: drawerOpen ? '0px' : '44px',
            sm: drawerOpen ? '0px' : '55px',
            md: drawerOpen ? '0px' : '0px',
          },
          zIndex: { xs: drawerOpen ? '11' : '1', sm: drawerOpen ? '11' : '1', md: drawerOpen ? '11' : '1', lg: 'unset' },
          transform: {
            xs: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
            md: 'none',
          },
        transitionProperty: 'width, transform, background-color, box-shadow',
        transitionDuration: '0.3s',
        transitionTimingFunction: 'ease-in-out'
      }}
      elevation={3}
    >
      <Box display="flex" alignItems="center" justifyContent={drawerOpen ? "end" : "start"}>
        {/* <Box display="flex" alignItems="center">
          {effectiveDrawerOpen && (
            <Box display="flex" flexDirection="column" alignItems="flex-start" ml={1}>
            </Box>
          )}
        </Box> */}
        {isSmallScreen && (
          <IconButton onClick={() => setDrawerOpen((prev) => !prev)}>
            {drawerOpen ? <ArrowBackIosIcon sx={{ fontSize: '16px' }} color="primary" /> : <ArrowForwardIosIcon  sx={{ fontSize: '16px' }} color="primary" />}
          </IconButton>
        )}
      </Box>
      {effectiveDrawerOpen && (
        <>
          {' '}
          {/* <Divider sx={{ my: 1 }} /> */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              mt:{xs:'8px',sm:'18px'},
            }}
          >
            <Box>
              {/* <Divider sx={{ my: 1 }} /> */}
              <Box sx={{ pt: 1 }}>
                <OrganisationDropdown
                  label="Organisation"
                  value={chatInputState?.selectedOrganisation ?? null}
                  onChange={(val) => {
                    setChatInputState((prev) => ({
                      ai_model: prev?.ai_model,
                      selectedOrganisation: val,
                    }));
                  }}
                  sx={{ width: '100%' }}
                />
              </Box>
              {chatInputState?.selectedOrganisation?.organisation_id && (
                <Box sx={{mt:{xs:'18px',sm:'18px'}}}>
                  <CampaignDropdown
                    label="Sector"
                    value={chatInputState?.selectedCampaign ?? null}
                    onChange={(val) => {
                      setChatInputState((prev) => ({
                        ai_model: prev?.ai_model,
                        selectedOrganisation: prev?.selectedOrganisation,
                        selectedCampaign: val,
                      }));

                      // setChatInputState((prev) => ({
                      //   ...prev,
                      //   selectedCampaign: val,
                      // }));
                    }}
                    organisationId={chatInputState.selectedOrganisation.organisation_id}
                    sx={{ width: '100%' }}
                    error={false}
                    errorMessage=""
                    disabled={false}
                  />
                </Box>
              )}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  mt:2,
                  mb:{xs:"0px",sm:1},
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  backgroundColor: '#E3F2FD', // Light blue
                  color: 'primary.main',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ cursor: 'pointer', fontWeight: 600, fontSize: '16px', lineHeight: '1' }}
                  onClick={() => handleToggleSection('projects')}
                >
                  {collapsedProjects ? <ExpandMore /> : <ExpandLess />} Projects
                </Typography>

                <Tooltip title="Add New Project" placement="top">
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => setOpenNewProjectModal(true)}
                    sx={{
                      width: 24,
                      height: 24,
                      border: '1px solid',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      padding: '4px',
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Collapse in={!collapsedProjects} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    cursor: 'pointer',
                    overflowY: 'auto',
                    maxHeight: '40vh',
                    pr: 1,
                    mb:{xs:"0px",sm:1},
                  }}
                >
                  <List dense component="nav" aria-label="project list">
                    {projects &&
                      projects[chatInputState?.selectedCampaign?.proposal_id]?.length === 0 && (
                        <Typography
                          variant="body2"
                          align="center"
                          sx={{ mt: 2, color: theme.palette.text.secondary }}
                        >
                          No projects found.
                        </Typography>
                      )}

                    {/* {projects && (projects[chatInputState?.selectedCampaign?.proposal_id] ?? [])?.map((project) => ( */}
                    {Array.isArray(projects?.[chatInputState?.selectedCampaign?.proposal_id]) &&
                      projects[chatInputState?.selectedCampaign?.proposal_id].map((project) => (
                        <Box key={project.project_id}>
                          <ListItem
                            disableGutters
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              mb: 1,
                              boxShadow: 1,
                              backgroundColor:
                                chatInputState?.selectedProject?.project_id === project?.project_id
                                  ? '#efefef' // Change this to your desired color
                                  : '#fff', // Change this to your desired color
                              '&:hover': {
                                backgroundColor:
                                  chatInputState?.selectedProject?.project_id ===
                                  project?.project_id
                                    ? '#dee2e6' // Change this to your desired hover color
                                    : '#eeeeee', // Change this to your desired hover color
                              },
                            }}
                            secondaryAction={
                              <Box display="flex" gap={0.5}>
                                <Tooltip title="Add Chat">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleAddChatToProject(project)}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="View Project Benchmark Report">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleViewBenchmarkReport('', project?.project_id)
                                    }
                                  >
                                    <InsertChartIcon
                                      fontSize="small"
                                      sx={{ color: theme.palette.primary.main }}
                                    />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Download Project Benchmark Report">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleDownloadBenchmarkReport('', project.project_id)
                                    }
                                  >
                                    <DownloadIcon fontSize="small" sx={{ color: '#4caf50' }} />
                                  </IconButton>
                                </Tooltip>
                                {/* <Tooltip title="Edit Project">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleEditProject(project?.project_id, project?.project_name)
                                    }
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Project">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteProject(project?.project_id)}
                                </Tooltip>                         >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip> */}
                                <Tooltip title="Toggle Chats">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleProjectChats(project)}
                                  >
                                    {visibleProjectId === project?.project_id ? (
                                      <ExpandLess />
                                    ) : (
                                      <ExpandMore />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            }
                          >
                            {editProjectId === project?.project_id ? (
                              <TextField
                                size="small"
                                value={editProjectName}
                                onChange={(e) => setEditProjectName(e.target.value)}
                                onBlur={handleSaveProjectName}
                                autoFocus
                                fullWidth
                              />
                            ) : (
                              <ListItemText
                                primary={
                                  project?.project_name?.length > 10
                                    ? project.project_name.slice(0, 10) + '...'
                                    : project?.project_name
                                }
                                primaryTypographyProps={{
                                  fontWeight: '500',
                                  fontSize: '14px',
                                  color: '#333333',
                                }}
                              />
                            )}
                          </ListItem>

                          <Collapse
                            in={visibleProjectId === project?.project_id}
                            timeout="auto"
                            unmountOnExit
                          >
                            <List dense sx={{ px: 1, py: 0 }}>
                              {projectAndQueryList?.[project?.project_id]?.length === 0 ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ pl: 2, mt: 1 }}
                                >
                                  No chats yet.
                                </Typography>
                              ) : (
                                projectAndQueryList?.[project?.project_id]?.map((chat) => (
                                  <ListItem
                                    key={chat.id || chat._id}
                                    selected={selectedQuery?.id === (chat.id || chat._id)}
                                    onClick={() => onSelectQuery(chat)}
                                    sx={{
                                      cursor: 'pointer',
                                      backgroundColor:
                                        selectedQuery?.conversation_id === chat?.conversation_id
                                          ? theme.palette.action.selected
                                          : theme.palette.grey[100],
                                      '&:hover': {
                                        backgroundColor:
                                          selectedQuery?.conversation_id === chat?.conversation_id
                                            ? theme.palette.action.selected
                                            : theme.palette.action.hover,
                                      },
                                      borderRadius: 1,
                                      px: 2,
                                      py: 0.5,
                                      mb: 0.5,
                                      ml: 0,
                                      transition: 'background-color 0.2s',
                                    }}
                                    secondaryAction={
                                      <Box display="flex" gap={0.5}>
                                        <Tooltip title="View Benchmarking Report">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleViewBenchmarkReport(
                                                chat.conversation_id,
                                                project.project_id
                                              );
                                            }}
                                          >
                                            <InsertChartIcon
                                              fontSize="small"
                                              sx={{ color: theme.palette.primary.main }}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Download Benchmark Report">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDownloadBenchmarkReport(
                                                chat.conversation_id,
                                                project.project_id
                                              );
                                            }}
                                          >
                                            <DownloadIcon
                                              fontSize="small"
                                              sx={{ color: '#4caf50' }}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    }

                                    // secondaryAction={
                                    //   editingProjectChat?.chatId === chat.id ? (
                                    //     <Box display="flex" gap={0.5}>
                                    //       <Tooltip title="Save">
                                    //         <IconButton
                                    //           size="small"
                                    //           onClick={handleSaveProjectChatName}
                                    //         >
                                    //           <SaveIcon fontSize="small" />
                                    //         </IconButton>
                                    //       </Tooltip>
                                    //       <Tooltip title="Cancel">
                                    //         <IconButton
                                    //           size="small"
                                    //           onClick={() => setEditingProjectChat(null)}
                                    //         >
                                    //           <CancelIcon fontSize="small" />
                                    //         </IconButton>
                                    //       </Tooltip>
                                    //     </Box>
                                    //   ) : (
                                    //     <Box display="flex" gap={0.5}>
                                    //       <Tooltip title="Edit Chat">
                                    //         <IconButton
                                    //           size="small"
                                    //           onClick={() =>
                                    //             setEditingProjectChat({
                                    //               projectId: project?.project_id,
                                    //               chatId: chat.id,
                                    //             })
                                    //           }
                                    //         >
                                    //           <EditIcon fontSize="small" />
                                    //         </IconButton>
                                    //       </Tooltip>
                                    //       <Tooltip title="Delete Chat">
                                    //         <IconButton
                                    //           size="small"
                                    //           onClick={() =>
                                    //             handleDeleteProjectChat(project?.project_id, chat.id)
                                    //           }
                                    //         >
                                    //           <DeleteIcon fontSize="small" />
                                    //         </IconButton>
                                    //       </Tooltip>
                                    //     </Box>
                                    //   )
                                    // }
                                  >
                                    <ListItemText
                                      primaryTypographyProps={{
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color:
                                          selectedQuery?.id === (chat.id || chat._id)
                                            ? '#333333'
                                            : '#333333',
                                      }}
                                      primary={
                                        (chat.query?.length ?? 0) > 15
                                          ? chat.query.slice(0, 15) + '...'
                                          : chat.query
                                      }
                                    />
                                  </ListItem>
                                ))
                              )}
                            </List>
                          </Collapse>
                        </Box>
                      ))}
                  </List>
                </Box>
              </Collapse>

              {/* <Divider sx={{ my: 1 }} /> */}

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  mb: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  backgroundColor: '#E3F2FD', // Light blue
                  color: 'primary.main',
                  // backgroundColor: '#E3F2FD',
                  // color: 'primary.main',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ cursor: 'pointer', fontWeight: 600, lineHeight: '1', fontSize: '16px' }}
                  onClick={() => handleToggleSection('chats')}
                >
                  {collapsedChats ? <ExpandMore /> : <ExpandLess />} Chats
                </Typography>

                <Tooltip title="Add New Chat" placement="top">
                  <IconButton
                    onClick={() => {
                      onInitiateNewChat();
                      setChatInputState((prevState) => ({
                        ...prevState,
                        query: '',
                        conversation_id: undefined,
                        selectedProject: null,
                      }));
                    }}
                    sx={{
                      width: 24,
                      height: 24,
                      border: '1px solid',
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: '50%',
                      },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Collapse in={!collapsedChats} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    cursor: 'pointer',
                    overflowY: 'auto',
                    height: '40vh',
                    //  maxHeight: {xs:'100%', sm:400, md:400},
                    pr: 1,
                  }}
                >
                  <List dense component="nav" aria-label="main mailbox folders">
                    {projectAndQueryList?.[chatInputState?.selectedCampaign?.proposal_id]
                      ?.length === 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ mt: 2 }}
                      >
                        No chats found.
                      </Typography>
                    )}

                    {(
                      projectAndQueryList?.[chatInputState?.selectedCampaign?.proposal_id] ?? []
                    )?.map((query) => (
                      <ListItem
                        key={query.conversation_id}
                        selected={selectedQuery?.conversation_id === query?.conversation_id}
                        onClick={() => onSelectQuery(query)}
                        secondaryAction={
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="View Benchmarking Report">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(
                                    `/r/ai/benchmarking-report?chat_id=${query.conversation_id}`,
                                    '_blank'
                                  );
                                }}
                              >
                                <InsertChartIcon
                                  fontSize="small"
                                  sx={{ color: theme.palette.primary.main }}
                                />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Download Benchmark Report">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadBenchmarkReport(query.conversation_id);
                                }}
                              >
                                <DownloadIcon fontSize="small" sx={{ color: '#4caf50' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        }
                        sx={{
                          cursor: 'pointer',
                          boxShadow: '0px 1px 2px 0px rgb(0 0 0 / 22%)',
                          mb: '6px',
                          borderRadius: 1,
                          backgroundColor:
                            selectedQuery?.conversation_id === query?.conversation_id
                              ? 'action.selected'
                              : 'white',
                          '&:hover': {
                            backgroundColor:
                              selectedQuery?.conversation_id === query?.conversation_id
                                ? 'action.selected'
                                : 'action.hover',
                          },
                        }}
                      >
                        <ListItemText
                          sx={{ fontWeight: 500, fontSize: '14px', color: '#333333' }}
                          primary={
                            <span style={{ fontSize: '14px', fontWeight: 500 }}>
                              {typeof query?.query === 'string'
                                ? query.query.length > 16
                                  ? query.query.slice(0, 16) + '...'
                                  : query.query
                                : JSON.stringify(query?.query)}
                            </span>
                          }
                          secondaryTypographyProps={{
                            fontSize: '0.75rem',
                            color: theme.palette.text.secondary,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </>
      )}
      <ModalForms
        show={openNewProjectModal}
        handleClose={() => setOpenNewProjectModal(false)}
        modalHeader={'Create Project'}
        size="sm"
        modalBody={
          <>
            <TextField
              fullWidth
              label="Project Name"
              size="small"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              sx={{ mt: 2 }}
            />
          </>
        }
        modalFooter={
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button size="small" onClick={() => setOpenNewProjectModal(false)} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleAddNewProject}
              disabled={!newProjectName}
            >
              Create
            </Button>
          </Box>
        }
      />
      {/* <Box
        sx={{
          mt: 2,
          px: 1,
          pb: 1.5,
          display: 'flex',
          justifyContent: 'center',
          position: 'sticky',
          bottom: 0,
          backgroundColor: theme.palette.background.paper,
          zIndex: 1,
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <Button
          sx={{ mt: 1 }}
          variant="contained"
          color="primary"
          size="small"
          fullWidth
          onClick={(e) => {
            e.preventDefault();
            window.open('/r/ai/benchmarking-report', '_blank');
            // navigate('/r/ai/benchmarking-report');
            console.log('Viewing Benchmarking Report...');
          }}
        >
          View Benchmarking Report
        </Button>
      </Box> */}
    </Paper>
  );
};

export default LeftSidebar;
